import React, { useEffect, useRef, useState } from 'react';

import { c } from 'ttag';

import { AuthenticationStore, Button, Modal, SkeletonLoader, useAuthentication } from '@freelancelabs/components';

import { recordMfaEnrolment } from '../../api/mfaEnrolment';
import PublicLayout from '../../app/content/PublicLayout';
import TotpPairing, { TOTP_CODE_LENGTH } from '../../app/mfa/TotpPairing';

interface Props {
    /** Names the entry in the authenticator app; empty falls back to the issuer alone. */
    accountName: string;
    /** Where to go once a factor is registered. */
    onDone: () => void;
}

/** MFA enrolment barrier: a non-dismissable screen (not a modal) shown when an account
 *  requires MFA and the person has no factor yet. Reuses the store's setupTotp/verifyTotpSetup. */
const MfaSetupPage = ({ accountName, onDone }: Props) => {
    const { setupTotp, verifyTotpSetup, logout, getAuthorizationToken } = useAuthentication() as AuthenticationStore;

    const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    /** The setup call failed; keep the raw message, it is what support needs. */
    const [broken, setBroken] = useState<string | null>(null);

    // StrictMode mounts twice: share the promise so both passes settle state. A boolean
    // ref that skipped the second pass would deadlock the screen (first pass cleaned up,
    // second skipped, state never set).
    const promesse = useRef<Promise<{ secret: string; uri: string }> | null>(null);

    useEffect(() => {
        if (!promesse.current) {
            promesse.current = setupTotp(accountName);
        }
        promesse.current
            .then(setSetup)
            .catch((failure: unknown) =>
                setBroken(failure instanceof Error ? `${failure.name}: ${failure.message}` : String(failure))
            );
    }, []);

    const verify = () => {
        if (busy || code.length < TOTP_CODE_LENGTH) {
            return;
        }
        setBusy(true);
        setError('');
        verifyTotpSetup(code)
            .then(async () => {
                // Record the enrolment event, not the state; silent, the factor is set regardless.
                const token = await getAuthorizationToken();
                if (token) {
                    recordMfaEnrolment(token);
                }
                onDone();
            })
            .catch(() => {
                setError(c('Mfa error').t`Invalid code.`);
            })
            .finally(() => setBusy(false));
    };

    return (
        // dismissable={false}: this is a condition of entry, not a choice; the only way
        // out is "Sign out" inside.
        <>
            {/* Keep PublicLayout behind the modal so it does not float over emptiness. */}
            <PublicLayout>{null}</PublicLayout>
            <Modal
                open
                dismissable={false}
                // Never called: dismissable neutralises both close paths.
                onClose={() => undefined}
                title={c('Mfa setup title').t`Set up two-factor authentication`}
                size={760}
                panelProps={{ className: 'mfa-modal' }}
            >
                <div className="flex flex-col gap-4">
                    <p className="text-sm">
                        {c('Mfa setup')
                            .t`Your organisation requires two-factor authentication. Set up an authenticator app to carry on: from now on, a code from that app will be asked for at every sign-in.`}
                    </p>

                    {setup === null && broken === null ? (
                        <div className="flex flex-col items-center gap-3 py-6" aria-busy="true">
                            <SkeletonLoader width={158} height={158} />
                            <SkeletonLoader width={220} height={16} />
                            <SkeletonLoader width={180} height={12} />
                        </div>
                    ) : broken !== null ? (
                        <>
                            <p className="text-sm text-danger" role="alert">
                                {c('Mfa setup')
                                    .t`The setup could not be started. This is not something you can fix from here: try again in a moment, and tell your Freelance.com contact if it persists.`}
                            </p>
                            {/* Raw technical message: it is what support will ask for. */}
                            {broken && <p className="text-xs text-text-hint break-words">{broken}</p>}
                            <Button category="weak" onClick={() => window.location.reload()}>
                                {c('Action').t`Try again`}
                            </Button>
                        </>
                    ) : (
                        setup && (
                            <form
                                name="mfa-setup-form"
                                method="post"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    verify();
                                }}
                                className="flex flex-col gap-4"
                            >
                                <TotpPairing
                                    uri={setup.uri}
                                    secret={setup.secret}
                                    code={code}
                                    onCode={setCode}
                                    error={error}
                                    busy={busy}
                                />
                                <Button type="submit" size="large" fullWidth loading={busy}>
                                    {c('Mfa setup action').t`Confirm and continue`}
                                </Button>
                            </form>
                        )
                    )}

                    <Button category="ghost" onClick={() => logout()}>
                        {c('Action').t`Sign out`}
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default MfaSetupPage;
